import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  IconButton,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { fetchRequest, clearState } from '../../redux/actions/events/record';
import { fetchRequest as fetchStreams } from '../../redux/actions/events/streams';
import config from '../../config';

/**
 * RecordEventDialog
 * =================
 *
 * Events are streams (like signals): a named stream — e.g. ``news_headlines`` —
 * holds many individual events. This dialog lets you pick an EXISTING stream
 * (or type a new one) and push an event into it.
 *
 * Two phases:
 *   1. A form: choose/create a stream + the event content.
 *   2. A post-submit panel with the programmatic `POST /v1/event/record`
 *      snippet (push events into the same stream from your own systems).
 *
 * On success it calls ``onRecorded()`` so the parent can refresh the list.
 */
const RecordEventDialog = ({ open, onClose, onRecorded }) => {
  const [stream, setStream] = useState('');
  const [text, setText] = useState('');
  // occurred_at = when the event actually happened (optional; defaults to now).
  // A FUTURE time schedules a known-future event (an earnings date, a planned
  // promo) — it overlays forecasts at its scheduled time and fires its full
  // reactions when that time comes. The recorded time ("now") is server-side.
  const [eventTime, setEventTime] = useState('');
  // Optional numeric payload (discount %, magnitude...) — used as the event's
  // severity when the text alone carries no scale.
  const [value, setValue] = useState('');
  const [copied, setCopied] = useState(false);

  const isFuture = Boolean(
    eventTime && new Date(eventTime).getTime() > Date.now(),
  );

  const dispatch = useDispatch();
  const { isLoading, response, error } = useSelector(
    (state) => state.event_record || {},
  );
  const streams = useSelector((state) => state.event_streams?.response) || [];

  // Load existing streams whenever the dialog opens so the picker is current.
  useEffect(() => {
    if (open) dispatch(fetchStreams());
  }, [open, dispatch]);

  useEffect(() => {
    if (response && onRecorded) {
      onRecorded();
    }
  }, [response, onRecorded]);

  const streamOptions = (streams || [])
    .map((s) => s.key)
    .filter(Boolean);

  const handleSubmit = (e) => {
    e.preventDefault();
    const json = {
      key: (stream || '').trim(),
      text: (text || '').trim(),
      origin: 'manual',
    };
    // occurred_at = when it happened (a future time schedules the event);
    // recorded time defaults to now server-side.
    if (eventTime) json.occurred_at = new Date(eventTime).toISOString();
    if (value !== '' && Number.isFinite(Number(value))) json.value = Number(value);
    dispatch(fetchRequest({ json }));
  };

  const handleClose = () => {
    setStream('');
    setText('');
    setEventTime('');
    setValue('');
    setCopied(false);
    dispatch(clearState());
    onClose();
  };

  const apiUrl = (config.API_URL || '').replace(/\/$/, '');
  const workspaceId = (typeof localStorage !== 'undefined'
    && localStorage.getItem('WORKSPACE_ID')) || '<WORKSPACE_ID>';
  const snippetStream = (stream || '').trim() || '<event_stream>';
  const curlSnippet = [
    `curl -X POST ${apiUrl}/event/record \\`,
    `  -H "Authorization: Bearer <YOUR_API_TOKEN>" \\`,
    `  -H "X-Workspace-Id: ${workspaceId}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"key": "${snippetStream}", "text": "<event description>", `
      + `"occurred_at": "<ISO8601, optional>"}'`,
  ].join('\n');

  const handleCopySnippet = () => {
    navigator.clipboard.writeText(curlSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Post-submit confirmation + programmatic snippet.
  if (response) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="success" />
            {response.scheduled ? 'Event scheduled' : 'Event recorded'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {response.scheduled ? (
                <>
                  Your known-future event was scheduled on stream{' '}
                  <strong>{response.stream || snippetStream}</strong>. It shows on
                  forecast charts as an upcoming marker, overlays forecasts once the
                  stream has a validated edge, and fires its full reactions when its
                  time comes.
                </>
              ) : (
                <>
                  Your event was pushed to stream <strong>{response.stream || snippetStream}</strong> and
                  is being scored (sentiment + relevance / importance / impact). It
                  appears in your Signals list with an Event badge within a second or two.
                </>
              )}
            </Typography>
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Event ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {response.event_id}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Push events programmatically (into any stream)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Use an API token (Settings → API tokens). The <code>key</code> is the
            event stream — reuse it to keep pushing into the same stream.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography
                component="pre"
                variant="body2"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1, m: 0 }}
              >
                {curlSnippet}
              </Typography>
              <IconButton onClick={handleCopySnippet} color={copied ? 'success' : 'default'} size="small">
                {copied ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BoltIcon color="error" />
          Record an event
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Events are streams — a named stream (e.g. <code>news_headlines</code>) holds
            many events. Pick an existing stream or create a new one, then push an
            event into it. Each event is scored for sentiment + impact.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Autocomplete
              freeSolo
              options={streamOptions}
              value={stream}
              onChange={(_e, v) => setStream(v || '')}
              onInputChange={(_e, v) => setStream(v || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event stream"
                  placeholder="Stream name"
                  required
                  helperText="Select an existing stream or type a new name to create one."
                  autoFocus
                />
              )}
            />
            <TextField
              fullWidth
              label="Event"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What happened?"
              multiline
              minRows={2}
              required
            />
            <TextField
              type="datetime-local"
              label="Occurred at"
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="When it happened (optional; defaults to now). A future time schedules a known-future event."
              fullWidth
            />
            <TextField
              type="number"
              label="Value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputProps={{ step: 'any' }}
              helperText="Optional numeric payload (discount %, magnitude…) — used as the event's weight when the text carries no scale."
              fullWidth
            />
            {isFuture && (
              <Alert severity="info">
                This occurs in the future, so it will be <strong>scheduled</strong>:
                the forecast chart marks it as upcoming, forecasts bend for it once
                the stream has a validated edge, and its full reactions (shock
                check, model features) fire at the scheduled time.
              </Alert>
            )}
            {error && (
              <Alert severity="error">
                {typeof error === 'string' ? error : (error.message || 'Failed to record event')}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !stream.trim() || !text.trim()}
          >
            {isLoading
              ? (isFuture ? 'Scheduling…' : 'Pushing…')
              : (isFuture ? 'Schedule event' : 'Push event')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RecordEventDialog;
